/**
 * FeatureGate - Conditionally renders content based on feature availability.
 *
 * Wraps content that requires a specific feature to be enabled.
 * Shows a loading state while checking, and a "Feature Disabled" message
 * if the feature is not enabled for the current study.
 */

import { ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { Construction, ArrowLeft, Settings } from 'lucide-react'
import { useStudyFeatures, FEATURE_KEYS } from '@/hooks/useFeatures'
import { useCurrentStudy } from '@/stores/studyStore'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

// Feature display names for user-friendly messages
const FEATURE_DISPLAY_NAMES: Record<string, string> = {
  [FEATURE_KEYS.ISSUES_TRACKER]: 'Issues Tracker',
  [FEATURE_KEYS.DATA_COMPARE]: 'Data Compare',
  [FEATURE_KEYS.PATIENT_PROFILE]: 'Patient Profile',
  [FEATURE_KEYS.AI_SUITE]: 'AI Suite',
  [FEATURE_KEYS.NARRATIVE]: 'eSafety Narrative',
}

interface FeatureGateProps {
  /** The feature key to check (e.g., 'issues_tracker') */
  feature: string
  /** Content to render when the feature is enabled */
  children: ReactNode
  /** Optional custom fallback when feature is disabled */
  fallback?: ReactNode
  /** If true, shows a loading spinner while checking. Default: true */
  showLoading?: boolean
}

/**
 * FeatureGate component that checks if a feature is enabled for the current study.
 *
 * @example
 * ```tsx
 * <FeatureGate feature="issues_tracker">
 *   <IssuesListPage />
 * </FeatureGate>
 * ```
 */
export function FeatureGate({
  feature,
  children,
  fallback,
  showLoading = true,
}: FeatureGateProps) {
  const { studyId: urlStudyId } = useParams<{ studyId: string }>()
  const currentStudy = useCurrentStudy()
  const studyId = urlStudyId || currentStudy?.studyId

  const { data, isLoading, error } = useStudyFeatures(studyId)

  // Loading state
  if (isLoading && showLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  // Error state - treat as disabled
  if (error) {
    console.error('Failed to load features:', error)
    return fallback || <FeatureDisabledPage feature={feature} />
  }

  // Check if feature is enabled
  const isEnabled = data?.enabledFeatures?.[feature] ?? false

  if (!isEnabled) {
    return fallback || <FeatureDisabledPage feature={feature} />
  }

  // Feature is enabled - render children
  return <>{children}</>
}

/**
 * Default "Feature Disabled" page shown when a feature is not enabled.
 */
interface FeatureDisabledPageProps {
  feature: string
}

export function FeatureDisabledPage({ feature }: FeatureDisabledPageProps) {
  const featureName = FEATURE_DISPLAY_NAMES[feature] || feature

  const handleGoBack = () => {
    window.history.back()
  }

  return (
    <div className="p-6">
      <Card className="max-w-2xl mx-auto mt-12">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 p-4 rounded-full bg-muted">
            <Construction className="h-12 w-12 text-muted-foreground" />
          </div>
          <CardTitle className="text-xl">Feature Not Enabled</CardTitle>
          <CardDescription className="text-base">
            The <strong>{featureName}</strong> feature is not enabled for this study.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center space-y-6">
          <p className="text-muted-foreground">
            This feature needs to be enabled by a system administrator before it can be used.
          </p>

          <div className="text-left bg-muted/50 rounded-lg p-4 space-y-2">
            <p className="font-medium flex items-center gap-2">
              <Settings className="h-4 w-4" />
              How to Enable
            </p>
            <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
              <li>Navigate to Study Settings</li>
              <li>Go to the Features tab</li>
              <li>Toggle on "{featureName}"</li>
              <li>Refresh this page</li>
            </ol>
          </div>

          <div className="flex justify-center gap-4">
            <Button variant="outline" onClick={handleGoBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default FeatureGate
